<script>
    import { page } from "$app/stores";
    import SidebarGroup from "/components/SidebarGroup.svelte";
    import SidebarToggle from "/components/SidebarToggle.svelte";
    import { goto as push } from "$app/navigation";
    import { ChevronDown } from "lucide-svelte";
    import Modal from "/components/Modal.svelte";
    import fetch, { logout } from "../util";

    let user = JSON.parse(localStorage.getItem("user"));
    let profileOpen = false;

    // 슈퍼관리자(admin + signup_source 미지정 또는 "cmyk")만 장비 관리(OID 포함)에 접근한다.
    // 일반관리자에게는 장비 관리 메뉴를 숨긴다.
    const isSuperAdmin =
        user?.Type === "admin" &&
        (!user?.SignupSource || user?.SignupSource === "cmyk");

    // 비밀번호 변경 모달 상태
    let showPwModal = false;
    let currentPassword = "";
    let newPassword = "";
    let newPasswordConfirm = "";
    let pwSubmitting = false;

    function toggleProfile(e) {
        e.stopPropagation();
        profileOpen = !profileOpen;
    }

    function closeProfile() {
        if (profileOpen) profileOpen = false;
    }

    function openPwModal() {
        currentPassword = "";
        newPassword = "";
        newPasswordConfirm = "";
        showPwModal = true;
        closeProfile();
    }

    function closePwModal() {
        showPwModal = false;
    }

    async function changePassword() {
        if (!currentPassword || !newPassword || !newPasswordConfirm) {
            alert("모든 항목을 입력해주세요.");
            return;
        }
        if (newPassword !== newPasswordConfirm) {
            alert("새 비밀번호와 비밀번호 확인이 일치하지 않습니다.");
            return;
        }

        pwSubmitting = true;
        try {
            const payload = { ...user, Password: currentPassword, NewPassword: newPassword };
            const response = await fetch(`/users/${user.ID}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.msg);
            }

            user = result;
            localStorage.setItem("user", JSON.stringify(result));
            alert("비밀번호가 변경되었습니다.");
            showPwModal = false;
        } catch (error) {
            console.error("Error:", error);
            alert("비밀번호 변경에 실패했습니다.");
        } finally {
            pwSubmitting = false;
        }
    }

    // 에러 로그(개발자 전용)는 GNB에 노출하지 않고, 로고를 2초 내 5번 클릭하면 진입한다.
    let logoClickTimes = [];
    function onLogoClick() {
        const now = Date.now();
        logoClickTimes = [...logoClickTimes, now].filter((t) => now - t < 2000);
        if (logoClickTimes.length >= 5) {
            logoClickTimes = [];
            push("/admin/error-logs");
        }
    }
</script>

<svelte:window on:click={closeProfile} on:keydown={(e) => { if (e.key === "Escape") closeProfile(); }} />

<nav id="desktop-sidebar" class="desktop-sidebar" aria-label="관리자 메뉴">
    <div class="sidebar-brand">
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
    <img id="gnb-logo" alt="" class="nav-logo" src="/assets/images/gnb_logo.png" on:click={onLogoClick} />
    <SidebarToggle />
    </div>
    <div id="sidebar-navigation" class="sidebar-scroll">
    <SidebarGroup id="gnb-partner-management" label="파트너 관리" paths={["/admin/partner","/admin/partner/usage-status","/admin/printer"]}>
            <ul>
                <li><a id="gnb-partner-status" href="/admin/partner" aria-current={$page.url.pathname === "/admin/partner" ? 'page' : undefined}>파트너 상태</a></li>
                <li><a id="gnb-partner-usage-status" href="/admin/partner/usage-status" aria-current={$page.url.pathname === "/admin/partner/usage-status" ? 'page' : undefined}>파트너 이용현황</a></li>
                <li><a id="gnb-partner-printer" href="/admin/printer" aria-current={$page.url.pathname === "/admin/printer" ? 'page' : undefined}>파트너 장비</a></li>
            </ul>
    </SidebarGroup>
    <SidebarGroup id="gnb-billing" label="과금" paths={["/admin/billing/fbkr","/admin/billing/partneron"]}>
            <ul>
                {#if isSuperAdmin}
                <li><a id="gnb-billing-fbkr" href="/admin/billing/fbkr" aria-current={$page.url.pathname === "/admin/billing/fbkr" ? 'page' : undefined}>FBKR 명세서</a></li>
                {/if}
                <li><a id="gnb-billing-partneron" href="/admin/billing/partneron" aria-current={$page.url.pathname === "/admin/billing/partneron" ? 'page' : undefined}>PartnerOn 계약현황</a></li>
            </ul>
    </SidebarGroup>
    {#if isSuperAdmin}
    <a id="gnb-equipment-management" href="/admin/management/printer" aria-current={$page.url.pathname === "/admin/management/printer" ? 'page' : undefined}  class="sidebar-home-link">장비 관리</a>
    {/if}
    <!-- 에러 로그: 개발자 전용 화면이라 GNB에서 숨김. 라우트(/admin/error-logs)는 유지되어 직접 URL로 접근 -->
    <!-- <a id="gnb-error-logs" href="/admin/error-logs" aria-current={$page.url.pathname === "/admin/error-logs" ? 'page' : undefined}  class="sidebar-home-link">에러 로그</a> -->

    </div>
    <div id="sidebar-account" class="sidebar-profile">
        <div class="profile-menu">
            <button id="gnb-profile-btn" class="profile-btn" aria-expanded={profileOpen} aria-controls="gnb-profile-menu" on:click={toggleProfile}>
                <span class="profile-avatar">{(user.Email?.[0] ?? '?').toUpperCase()}</span>
                <span class="profile-identity"><span class="profile-name">{user.Name || user.Email}</span><span class="profile-caption">내 계정</span></span>
                <span class="profile-chevron" class:rotated={profileOpen}><ChevronDown size={14} /></span>
            </button>
            {#if profileOpen}
            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
            <div id="gnb-profile-menu" class="profile-dropdown" role="presentation" on:click|stopPropagation>
                <div class="profile-dropdown-email">{user.Email}</div>
                <ul>
                    <li><button id="gnb-change-password" class="textButton profile-menu-btn" on:click={openPwModal}>비밀번호 변경</button></li>
                    <li><button id="gnb-logout" class="textButton profile-menu-btn" on:click={logout}>로그아웃</button></li>
                </ul>
            </div>
            {/if}
        </div>
    </div>
</nav>

<Modal showModal={showPwModal} close={closePwModal} title="비밀번호 변경" useClose={true}>
    <div class="modal-container" style="padding: 10px 10px;">
        <div>
            <label for="pw-current">현재 비밀번호</label>
            <input id="pw-current" type="password" bind:value={currentPassword} required />
        </div>
        <div>
            <label for="pw-new">새 비밀번호</label>
            <input id="pw-new" type="password" bind:value={newPassword} required />
        </div>
        <div>
            <label for="pw-confirm">비밀번호 확인</label>
            <input id="pw-confirm" type="password" bind:value={newPasswordConfirm} required />
        </div>
        <div style="display: flex; justify-content: flex-end; margin-top: 1rem; gap: 1rem;">
            <button id="btn-cancel" class="buttonA btn-cancel" on:click={closePwModal}>취소</button>
            <button id="btn-confirm" class="buttonA" on:click={changePassword} disabled={pwSubmitting}>변경</button>
        </div>
    </div>
</Modal>
